import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../../api/http";
import {
  callOutShift,
  clockInShift,
  clockOutShift,
  endBreakShift,
  fetchStaffShift,
  requestShift,
  startBreakShift,
  submitTimeCorrection,
  type StaffShiftDetail,
} from "../../../api/staffApi";
import { formatHourlyRate, formatShiftRange, formatTime } from "../staffFormat";
import { CallOutSheet } from "../components/CallOutSheet";
import { ConfirmationSheet } from "../components/ConfirmationSheet";
import { ShiftStatusBadge } from "../components/ShiftStatusBadge";
import { SkeletonCard } from "../components/SkeletonCard";
import { StaffAlert } from "../components/StaffAlert";
import { StaffAppShell } from "../components/StaffAppShell";
import { StickyActionBar } from "../components/StickyActionBar";
import { TimeCorrectionSheet } from "../components/TimeCorrectionSheet";

export function StaffShiftDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shift, setShift] = useState<StaffShiftDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRequest, setConfirmRequest] = useState(false);
  const [callOutOpen, setCallOutOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setShift(await fetchStaffShift(id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load shift.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const runAction = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const canRequest =
    shift?.status === "open" && !shift.requestStatus && shift.assignmentStatus !== "active";
  const isAssigned = shift?.assignmentStatus === "active" && !shift.calledOut;
  const tr = shift?.timeRecord;
  const clockedIn = Boolean(tr?.recordedClockIn);
  const clockedOut = Boolean(tr?.recordedClockOut);

  let primaryAction: React.ReactNode = null;
  if (shift && canRequest) {
    primaryAction = (
      <button type="button" className="primary-button staff-sticky-btn" onClick={() => setConfirmRequest(true)}>
        Request shift
      </button>
    );
  } else if (shift && isAssigned && !clockedIn) {
    primaryAction = (
      <button
        type="button"
        className="primary-button staff-sticky-btn"
        disabled={busy}
        onClick={() => void runAction(() => clockInShift(shift.id))}
      >
        Clock in
      </button>
    );
  } else if (shift && isAssigned && clockedIn && !clockedOut) {
    primaryAction = (
      <div className="staff-sticky-actions">
        <button
          type="button"
          className="secondary-button"
          disabled={busy}
          onClick={() => void runAction(() => startBreakShift(shift.id))}
        >
          Start break
        </button>
        <button
          type="button"
          className="secondary-button"
          disabled={busy}
          onClick={() => void runAction(() => endBreakShift(shift.id))}
        >
          End break
        </button>
        <button
          type="button"
          className="primary-button"
          disabled={busy}
          onClick={() => void runAction(() => clockOutShift(shift.id))}
        >
          Clock out
        </button>
      </div>
    );
  }

  return (
    <StaffAppShell title="Shift" showBack backTo="/staff/shifts" hideNav>
      <div className="staff-page staff-page--with-bar">
        {error ? <StaffAlert onDismiss={() => setError(null)}>{error}</StaffAlert> : null}

        {loading ? (
          <SkeletonCard />
        ) : shift ? (
          <>
            <div className="staff-detail-head">
              <ShiftStatusBadge shift={shift} />
              <h2 className="staff-page-title">{shift.roleName}</h2>
              <p className="staff-detail-event">{shift.eventName}</p>
              <p>{formatShiftRange(shift.scheduledStart, shift.scheduledEnd, shift.timezone)}</p>
              {shift.startingLocation ? <p className="muted">{shift.startingLocation}</p> : null}
              <p className="muted">{formatHourlyRate(shift.hourlyRate)}</p>
            </div>

            {tr ? (
              <section className="staff-section">
                <h3 className="staff-section__title">Time record</h3>
                <ul className="staff-kv-list">
                  <li>
                    <span className="muted">Clock in</span>
                    <span>{tr.recordedClockIn ? formatTime(tr.recordedClockIn) : "—"}</span>
                  </li>
                  <li>
                    <span className="muted">Clock out</span>
                    <span>{tr.recordedClockOut ? formatTime(tr.recordedClockOut) : "—"}</span>
                  </li>
                </ul>
                {clockedOut && tr.id ? (
                  <button type="button" className="staff-text-button" onClick={() => setCorrectionOpen(true)}>
                    Request time correction
                  </button>
                ) : null}
              </section>
            ) : null}

            {isAssigned && !shift.calledOut ? (
              <button type="button" className="staff-text-button staff-text-button--danger" onClick={() => setCallOutOpen(true)}>
                Call out of this shift
              </button>
            ) : null}
          </>
        ) : (
          <StaffAlert>Shift not found.</StaffAlert>
        )}
      </div>

      {primaryAction ? <StickyActionBar>{primaryAction}</StickyActionBar> : null}

      <ConfirmationSheet
        open={confirmRequest}
        title="Request this shift?"
        message="Management will review your request. You can track status on this page."
        confirmLabel="Send request"
        busy={busy}
        onCancel={() => setConfirmRequest(false)}
        onConfirm={() => {
          if (!shift) return;
          void runAction(async () => {
            await requestShift(shift.id);
            setConfirmRequest(false);
          });
        }}
      />

      <CallOutSheet
        open={callOutOpen}
        busy={busy}
        onClose={() => setCallOutOpen(false)}
        onSubmit={(payload) => {
          if (!shift) return;
          void runAction(async () => {
            await callOutShift(shift.id, payload);
            setCallOutOpen(false);
            navigate("/staff/shifts");
          });
        }}
      />

      <TimeCorrectionSheet
        open={correctionOpen}
        shiftTimeRecordId={tr?.id ?? null}
        busy={busy}
        onClose={() => setCorrectionOpen(false)}
        onSubmit={(payload) => {
          void runAction(async () => {
            await submitTimeCorrection(payload);
            setCorrectionOpen(false);
          });
        }}
      />
    </StaffAppShell>
  );
}
